import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { useFleetSchedule, rangeFromPreset } from "../lib/fleetCalendar";
import { RangePresetSwitcher } from "../components/fleet-calendar/RangePresetSwitcher";
import { FilterBar } from "../components/fleet-calendar/FilterBar";
import { AvailabilitySummaryStrip } from "../components/fleet-calendar/AvailabilitySummaryStrip";
import { CalendarGrid } from "../components/fleet-calendar/CalendarGrid";
import { MobileDayView } from "../components/fleet-calendar/MobileDayView";
import { BookingBlockPopover } from "../components/fleet-calendar/BookingBlockPopover";
import { SuggestedVehiclesPanel } from "../components/fleet-calendar/SuggestedVehiclesPanel";

const EMPTY_VEHICLES = Object.freeze([]);

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export default function FleetCalendar() {
  const nav = useNavigate();
  const [presetId, setPresetId] = useState("7d");
  const [anchorDate, setAnchorDate] = useState(() => startOfDay(new Date()));
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState([]);
  const [availabilityState, setAvailabilityState] = useState([]);
  const [bookingStatus, setBookingStatus] = useState([]);
  const [openBookingId, setOpenBookingId] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(false);

  const { start, end } = useMemo(() => rangeFromPreset(presetId, anchorDate), [presetId, anchorDate]);

  const { schedule, summary, loading, reload } = useFleetSchedule({
    start, end, category, availabilityState, bookingStatus, search,
    paused: !!openBookingId || suggestOpen,
  });

  const vehicles = schedule?.vehicles || EMPTY_VEHICLES;
  const categoryOptions = useMemo(
    () => Array.from(new Set(vehicles.map((v) => v.category).filter(Boolean))).sort(),
    [vehicles]
  );

  const goToBooking = (bookingId) => setOpenBookingId(bookingId);
  const goToVehicle = (vehicleId) => nav(`/app/vehicles/${vehicleId}`);
  const goToEmptySlot = (vehicleId, slotStart, slotEnd) => {
    nav(`/app/bookings/new?vehicle_id=${vehicleId}&start=${encodeURIComponent(slotStart.toISOString())}&end=${encodeURIComponent(slotEnd.toISOString())}`);
  };

  const shiftMobileDay = (delta) => {
    setPresetId("today");
    setAnchorDate((d) => startOfDay(new Date(d.getTime() + delta * 24 * 3600 * 1000)));
  };

  return (
    <div className="space-y-4" data-testid="fleet-calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Fleet Calendar</h1>
          <p className="text-slate-600 mt-1 text-sm">See the whole fleet's schedule and availability at a glance.</p>
        </div>
        <button
          type="button" onClick={() => setSuggestOpen(true)} data-testid="fc-find-vehicle-btn"
          className="px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50/60 text-emerald-800 text-sm font-medium inline-flex items-center gap-1.5 hover:bg-emerald-100"
        >
          <Sparkles className="w-4 h-4" /> Find available vehicle
        </button>
      </div>

      <AvailabilitySummaryStrip summary={summary} loading={loading} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <RangePresetSwitcher value={presetId} onChange={(id) => setPresetId(id)} />
        <div className="md:hidden inline-flex items-center gap-1">
          <button type="button" onClick={() => shiftMobileDay(-1)} data-testid="fc-mobile-prev-day"
                  className="grid min-h-11 min-w-11 place-items-center rounded-lg border border-slate-300 bg-white"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-medium px-2">{anchorDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
          <button type="button" onClick={() => shiftMobileDay(1)} data-testid="fc-mobile-next-day"
                  className="grid min-h-11 min-w-11 place-items-center rounded-lg border border-slate-300 bg-white"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <FilterBar
        search={search} setSearch={setSearch}
        category={category} setCategory={setCategory}
        availabilityState={availabilityState} setAvailabilityState={setAvailabilityState}
        bookingStatus={bookingStatus} setBookingStatus={setBookingStatus}
        categoryOptions={categoryOptions}
      />

      {loading && !schedule && (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="fc-loading">
          <SearchIcon className="w-5 h-5 mx-auto mb-2 text-slate-400" />
          Loading schedule…
        </div>
      )}

      {schedule && (
        <>
          <div className="hidden md:block">
            <CalendarGrid
              vehicles={vehicles} rangeStart={start} rangeEnd={end}
              onOpenBooking={goToBooking} onOpenIssue={goToBooking}
              onEmptySlot={goToEmptySlot} onOpenVehicle={goToVehicle}
            />
          </div>
          <div className="md:hidden">
            <MobileDayView
              vehicles={vehicles} day={anchorDate}
              onOpenBooking={goToBooking} onOpenIssue={goToBooking}
              onEmptySlot={goToEmptySlot} onOpenVehicle={goToVehicle}
            />
          </div>
        </>
      )}

      <BookingBlockPopover
        bookingId={openBookingId} open={!!openBookingId}
        onClose={() => setOpenBookingId(null)}
        onChanged={() => reload()}
      />

      <SuggestedVehiclesPanel
        open={suggestOpen} onClose={() => setSuggestOpen(false)}
        start={start} end={end} category={category[0] || null}
      />
    </div>
  );
}
